import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import "../../assets/generalTrade.css";

interface OfflineProduct {
  id: number;
  productName: string;
  SKU?: string | null;
  brand_name?: string | null;
  category_name?: string | null;
  subcategory_name?: string | null;
  image_path?: string | null;
}

interface ProfileSlide {
  title: string;
  image: string;
}

const HOUSE_OF_TASTE_SLIDES: ProfileSlide[] = [
  { title: "Bringing Quality Essentials Closer to Every Home", image: "/images/general-trade/house-of-taste/slide-01.webp" },
  { title: "Product Details", image: "/images/general-trade/house-of-taste/slide-02.webp" },
  { title: "Customer Outreach", image: "/images/general-trade/house-of-taste/slide-03.webp" },
  { title: "In Store", image: "/images/general-trade/house-of-taste/slide-04.webp" },
  { title: "At Home", image: "/images/general-trade/house-of-taste/slide-05.webp" },
  { title: "Unique Proposition", image: "/images/general-trade/house-of-taste/slide-06.webp" },
  { title: "Visionary Forecast", image: "/images/general-trade/house-of-taste/slide-07.webp" },
  { title: "Our Target", image: "/images/general-trade/house-of-taste/slide-08.webp" },
  { title: "Why Partner With Us", image: "/images/general-trade/house-of-taste/slide-09.webp" },
  { title: "Contact Us", image: "/images/general-trade/house-of-taste/slide-10.webp" },
];

const GeneralTradeProduct: React.FC = () => {
  const BASE = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const { productId } = useParams<{ productId: string }>();

  const [product, setProduct] = useState<OfflineProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSlideIndex, setSelectedSlideIndex] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadProduct = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${BASE}/general-trade/products`);
        const rows: OfflineProduct[] = response.data?.data || [];
        const found = rows.find((item) => Number(item.id) === Number(productId)) || null;
        if (!cancelled) setProduct(found);
      } catch (error) {
        console.error("Unable to load General Trade product:", error);
        if (!cancelled) setProduct(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadProduct();
    return () => {
      cancelled = true;
    };
  }, [BASE, productId]);

  useEffect(() => {
    if (selectedSlideIndex === null) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSelectedSlideIndex(null);
      if (event.key === "ArrowRight") {
        setSelectedSlideIndex((prev) =>
          prev === null ? null : Math.min(prev + 1, HOUSE_OF_TASTE_SLIDES.length - 1),
        );
      }
      if (event.key === "ArrowLeft") {
        setSelectedSlideIndex((prev) =>
          prev === null ? null : Math.max(prev - 1, 0),
        );
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [selectedSlideIndex]);

  const hasTemporaryProfile = useMemo(() => {
    const name = String(product?.productName || "").toLowerCase();
    const brand = String(product?.brand_name || "").toLowerCase();

    return (
      name.includes("atta") ||
      name.includes("house of taste") ||
      brand.includes("house of taste")
    );
  }, [product]);

  if (loading) {
    return <main className="gt-page"><div className="gt-loading">Loading product...</div></main>;
  }

  if (!product) {
    return <main className="gt-page"><div className="gt-loading">General Trade product not found.</div></main>;
  }

  return (
    <main className="gt-page">
      <section className="gt-page-banner gt-product-banner">
        <div className="gt-container">
          <span className="gt-eyebrow">{product.brand_name || "General Trade Product"}</span>
          <h1>{product.productName}</h1>
          <p>
            {[product.category_name, product.subcategory_name, product.SKU]
              .filter(Boolean)
              .join(" • ")}
          </p>
        </div>
      </section>

      <section className="gt-content-section">
        <div className="gt-container">
          {hasTemporaryProfile ? (
            <>
              <div className="gt-section-heading gt-section-heading-left">
                <span>Product Profile</span>
                <h2>Explore {product.productName}</h2>
                <p>Select any section to view the complete presentation slide.</p>
              </div>

              <div className="gt-profile-grid">
                {HOUSE_OF_TASTE_SLIDES.map((slide, index) => (
                  <button
                    key={slide.image}
                    type="button"
                    className="gt-profile-card"
                    onClick={() => setSelectedSlideIndex(index)}
                  >
                    <div className="gt-profile-thumb">
                      <img src={slide.image} alt={slide.title} />
                      <span>View Slide</span>
                    </div>
                    <div className="gt-profile-card-body">
                      <small>Section {String(index + 1).padStart(2, "0")}</small>
                      <h3>{slide.title}</h3>
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="gt-coming-card">
              <h2>{product.productName}</h2>
              <p>The public presentation for this Offline product will be added here.</p>
            </div>
          )}
        </div>
      </section>

      {selectedSlideIndex !== null && (
        <div
          className="gt-slide-modal"
          role="dialog"
          aria-modal="true"
          aria-label={HOUSE_OF_TASTE_SLIDES[selectedSlideIndex].title}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setSelectedSlideIndex(null);
          }}
        >
          <div className="gt-slide-modal-panel">
            <div className="gt-slide-modal-header">
              <div>
                <small>
                  {selectedSlideIndex + 1} / {HOUSE_OF_TASTE_SLIDES.length}
                </small>
                <h3>{HOUSE_OF_TASTE_SLIDES[selectedSlideIndex].title}</h3>
              </div>
              <button type="button" onClick={() => setSelectedSlideIndex(null)} aria-label="Close slide">×</button>
            </div>

            <div className="gt-slide-modal-image-wrap">
              <img
                src={HOUSE_OF_TASTE_SLIDES[selectedSlideIndex].image}
                alt={HOUSE_OF_TASTE_SLIDES[selectedSlideIndex].title}
              />
            </div>

            <div className="gt-slide-modal-nav">
              <button
                type="button"
                disabled={selectedSlideIndex === 0}
                onClick={() => setSelectedSlideIndex((prev) => (prev === null ? null : Math.max(prev - 1, 0)))}
              >
                ← Previous
              </button>
              <button
                type="button"
                disabled={selectedSlideIndex === HOUSE_OF_TASTE_SLIDES.length - 1}
                onClick={() => setSelectedSlideIndex((prev) => (prev === null ? null : Math.min(prev + 1, HOUSE_OF_TASTE_SLIDES.length - 1)))}
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default GeneralTradeProduct;
